import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fp40iaImages } from '../assets';
import Countdown from '../components/Countdown';
import { projets } from '../data/siteData';
import { getPublicSettings } from '../services/contentApi';
import './ForprodeDetail.css';

const GOOGLE_FORM_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSeittPYKLmcRMvH8lyfzxdMcx386OUvAKT9vedgdGVIJjeycA/viewform';

const curriculum = [
  {
    week: 'S1–S2',
    title: 'Fondamentaux Python & Maths',
    items: ['Python pour la data', 'Algèbre linéaire appliquée', 'Statistiques descriptives', 'Environnements ML (Jupyter, Colab)'],
  },
  {
    week: 'S3–S5',
    title: 'Machine Learning Supervisé',
    items: ['Régression & classification', 'Arbres de décision, Random Forest', 'Évaluation de modèles', 'Scikit-learn & pipelines'],
  },
  {
    week: 'S6–S9',
    title: 'IA Générative & LLMs',
    items: ['Transformers & attention', 'Fine-tuning de modèles', 'Prompt engineering avancé', 'Langchain & RAG'],
  },
  {
    week: 'S10–S12',
    title: 'Projet & Insertion',
    items: ['Projet final en conditions réelles', 'Déploiement de modèles', 'Présentation devant jury', 'Préparation aux entretiens'],
  },
];

const faqItems = [
  {
    q: 'Qui peut candidater au programme FORPRODE 40 IA ?',
    a: 'Le programme est ouvert à tous les jeunes de 18 à 35 ans résidant en Côte d\'Ivoire. Aucun prérequis technique strict n\'est exigé, mais une appétence pour la logique et les mathématiques est un atout. Le test de positionnement permet d\'évaluer votre potentiel.',
  },
  {
    q: 'Quel est le coût de la formation ?',
    a: 'Le programme FORPRODE 40 IA est co-financé par nos partenaires institutionnels (GIZ, FORPRODE). Les participants bénéficient d\'une prise en charge partielle ou totale selon leur profil. Les détails de financement sont communiqués lors de l\'entretien de sélection.',
  },
  {
    q: 'Comment se déroule le processus de sélection ?',
    a: 'La sélection se déroule en 3 étapes : (1) Candidature en ligne via le formulaire Google, (2) Test de positionnement en ligne (logique, maths, motivation), (3) Entretien individuel de 20 minutes avec l\'équipe TTA. Les résultats sont communiqués sous 5 jours ouvrables.',
  },
  {
    q: 'La formation est-elle en présentiel ou en ligne ?',
    a: 'Le programme se déroule en présentiel à Abidjan, 5 jours par semaine. Des ressources en ligne viennent compléter les sessions en salle. Pour les candidats hors d\'Abidjan, une aide à l\'hébergement peut être envisagée sous conditions.',
  },
  {
    q: 'Quelle certification obtient-on à la fin du programme ?',
    a: 'Les diplômés reçoivent une certification TTA en GenAI & Machine Learning reconnue par les entreprises partenaires. Le programme prépare également aux certifications internationales de niveau Azure AI ou Google Cloud ML selon le profil.',
  },
  {
    q: 'Y a-t-il un accompagnement à l\'emploi après la formation ?',
    a: 'Oui. Un accompagnement de 8 semaines est inclus : optimisation CV/LinkedIn, coaching entretiens techniques, mise en relation avec le réseau d\'entreprises partenaires de TTA. Notre taux d\'insertion est de 85% dans les 3 mois suivant la fin du programme.',
  },
];

export default function ForprodeDetail() {
  const projet = projets.find(p => p.id === 'forprode-40-ia');
  if (!projet) return null;

  const [openFaq, setOpenFaq] = useState(null);
  const [cmsSettings, setCmsSettings] = useState(null);
  useEffect(() => { getPublicSettings().then(d => { if (d) setCmsSettings(d); }); }, []);
  const countdownDate = cmsSettings?.forprode_date || projet.candidatureDate;
  const countdownMode = cmsSettings?.forprode_mode || projet.candidatureMode;
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [formSent, setFormSent] = useState(false);

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setFormSent(true);
  };

  return (
    <main className="forprode-page">

      {/* ── HERO ─────────────────────────────────────────── */}
      <section className="forprode-page__hero">
        <div className="forprode-page__hero-bg" />
        <div className="forprode-page__hero-grid" />
        <div className="forprode-page__hero-inner">
          <div className="forprode-page__hero-left">
            <div className="forprode-page__badge animate-fade-up">
              <span className="forprode-page__badge-dot" />
              <span>Programme en cours · 2026</span>
            </div>
            <h1 className="forprode-page__h1 animate-fade-up-d1">
              Bootcamp<br /><span className="grad-text">GenAI & Machine Learning</span>
            </h1>
            <p className="forprode-page__sub animate-fade-up-d2">
              Formation intensive de 12 semaines aux métiers de l'IA générative et du Machine Learning. Un programme d'excellence co-financé par FORPRODE et porté par TTA.
            </p>
            <div className="forprode-page__ctas animate-fade-up-d3">
              <a href={GOOGLE_FORM_URL} target="_blank" rel="noopener noreferrer" className="btn-primary" style={{ fontSize: '14px', padding: '14px 28px' }}>
                Candidater au programme →
              </a>
              <a href="#programme" className="btn-ghost">En savoir plus ↓</a>
            </div>
            <div className="forprode-page__stats animate-fade-up-d3">
              {[
                { val: '50', lbl: 'Places disponibles' },
                { val: '12', lbl: 'Semaines intensives' },
                { val: '1', lbl: 'Spécialisation' },
                { val: '100%', lbl: 'Certifiant' },
              ].map((s, i) => (
                <div key={s.lbl} className="forprode-page__stat-group">
                  {i > 0 && <div className="forprode-page__stat-divider" />}
                  <div className="forprode-page__stat">
                    <span className="grad-text" style={{ fontFamily: "'Sora',sans-serif", fontSize: '28px', fontWeight: 800 }}>{s.val}</span>
                    <span style={{ fontSize: '11px', color: 'rgba(255,255,255,.5)' }}>{s.lbl}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="forprode-page__hero-right animate-slide-right">
            <Countdown targetDate={countdownDate} mode={countdownMode} />
            <div className="forprode-page__info-card">
              <div className="forprode-page__info-item">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#8BC53F" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>
                <span>Durée : 12 semaines intensives</span>
              </div>
              <div className="forprode-page__info-item">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#8BC53F" strokeWidth="2" strokeLinecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                <span>Lieu : Abidjan, Côte d'Ivoire</span>
              </div>
              <div className="forprode-page__info-item">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#8BC53F" strokeWidth="2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
                <span>50 places disponibles</span>
              </div>
              <div className="forprode-page__info-item">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#8BC53F" strokeWidth="2" strokeLinecap="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                <span>Certification internationale incluse</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── À PROPOS ─────────────────────────────────────── */}
      <section className="section" id="programme">
        <div className="fp-about">
          <div className="fp-about__text">
            <div className="section-eyebrow">Le programme</div>
            <h2 className="section-title" style={{ fontSize: '32px' }}>Une formation d'excellence<br />en intelligence artificielle</h2>
            <p style={{ color: '#555', lineHeight: 1.75, marginBottom: '20px' }}>
              FORPRODE 40 IA est une initiative d'excellence visant à former 50 jeunes talents aux métiers de l'IA de demain. À travers un bootcamp intensif de 12 semaines, les participants acquièrent des compétences pointues en IA générative et Machine Learning.
            </p>
            <p style={{ color: '#555', lineHeight: 1.75, marginBottom: '24px' }}>
              Chaque apprenant bénéficie d'un accompagnement personnalisé vers l'insertion professionnelle, avec un suivi post-formation et une mise en relation avec notre réseau d'entreprises partenaires.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                'Formation intensive 5 jours/semaine',
                'Projets pratiques en conditions réelles',
                'Certification internationale en fin de parcours',
                'Accompagnement à l\'insertion professionnelle',
                'Accès au réseau alumni TTA',
                'Suivi post-formation de 3 mois',
              ].map(item => (
                <div key={item} style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--mid)" strokeWidth="2.5" strokeLinecap="round" style={{ flexShrink: 0 }}>
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  <span style={{ color: '#333' }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="fp-about__img-wrap">
            <img
              src={fp40iaImages.img002}
              alt="FORPRODE 40 IA"
              className="fp-about__img"
            />
          </div>
        </div>
      </section>

      {/* ── CURRICULUM ───────────────────────────────────── */}
      <section className="section section-alt">
        <div className="section-eyebrow">Curriculum</div>
        <h2 className="section-title">Programme semaine par semaine</h2>
        <p className="section-subtitle">Un parcours structuré en 4 blocs progressifs pour maîtriser l'IA générative et le Machine Learning.</p>
        <div className="fp-curriculum">
          {curriculum.map((block, i) => (
            <div key={i} className="fp-curriculum__card">
              <div className="fp-curriculum__week">{block.week}</div>
              <div className="fp-curriculum__title">{block.title}</div>
              <ul className="fp-curriculum__list">
                {block.items.map(item => (
                  <li key={item}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--mid)" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* ── PROCESSUS ────────────────────────────────────── */}
      <section className="section">
        <div className="section-eyebrow">Processus de sélection</div>
        <h2 className="section-title">Comment intégrer le programme ?</h2>
        <div className="fp-processus-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginTop: '8px' }}>
          {[
            { step: '01', title: 'Candidature en ligne', desc: 'Remplissez le formulaire Google. Gratuit et ouvert à tous.' },
            { step: '02', title: 'Test de positionnement', desc: 'Évaluation de vos connaissances et motivations. Sans prérequis.' },
            { step: '03', title: 'Entretien de sélection', desc: 'Un échange de 20 min pour valider votre projet et votre motivation.' },
            { step: '04', title: 'Intégration au programme', desc: 'Confirmation, préparation et démarrage du bootcamp intensif.' },
          ].map(s => (
            <div key={s.step} style={{ background: '#f8faf8', border: '0.5px solid #e8e8e8', borderRadius: '12px', padding: '20px 18px' }}>
              <div style={{ fontFamily: "'Sora',sans-serif", fontSize: '28px', fontWeight: 800, background: 'var(--grad)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', marginBottom: '10px' }}>{s.step}</div>
              <div style={{ fontFamily: "'Sora',sans-serif", fontSize: '14px', fontWeight: 700, color: '#111', marginBottom: '6px' }}>{s.title}</div>
              <div style={{ fontSize: '13px', color: '#666', lineHeight: 1.55 }}>{s.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── CONTACT FORM ─────────────────────────────────── */}
      <section className="section section-alt" id="contact" style={{ paddingBottom: 0 }}>
        <div className="fp-contact">
          <div className="fp-contact__left">
            <div className="section-eyebrow">Nous contacter</div>
            <h2 className="section-title" style={{ fontSize: '30px' }}>Une question sur le programme ?</h2>
            <p style={{ color: '#555', lineHeight: 1.75, marginBottom: '28px' }}>
              Notre équipe répond sous 24h. Pour candidater directement, utilisez le formulaire Google officiel.
            </p>
            {formSent ? (
              <div className="fp-form-success">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--mid)" strokeWidth="2" strokeLinecap="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                <div>
                  <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, color: '#111', marginBottom: '4px' }}>Message envoyé !</div>
                  <div style={{ fontSize: '14px', color: '#666' }}>Nous vous répondrons dans les 24 heures.</div>
                </div>
              </div>
            ) : (
              <form className="fp-form" onSubmit={handleFormSubmit}>
                <div className="fp-form__row">
                  <div className="fp-form__group">
                    <label className="fp-form__label">Nom complet *</label>
                    <input className="fp-form__input" type="text" placeholder="Votre nom" required value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} />
                  </div>
                  <div className="fp-form__group">
                    <label className="fp-form__label">Email *</label>
                    <input className="fp-form__input" type="email" placeholder="votre@email.com" required value={formData.email} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} />
                  </div>
                </div>
                <div className="fp-form__row">
                  <div className="fp-form__group">
                    <label className="fp-form__label">Téléphone</label>
                    <input className="fp-form__input" type="tel" placeholder="+225 00 00 00 00" value={formData.phone} onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))} />
                  </div>
                  <div className="fp-form__group">
                    <label className="fp-form__label">Sujet *</label>
                    <select className="fp-form__input" required value={formData.subject} onChange={e => setFormData(p => ({ ...p, subject: e.target.value }))}>
                      <option value="">Choisir un sujet</option>
                      <option value="candidature">Processus de candidature</option>
                      <option value="financement">Financement & bourses</option>
                      <option value="programme">Contenu du programme</option>
                      <option value="autre">Autre question</option>
                    </select>
                  </div>
                </div>
                <div className="fp-form__group">
                  <label className="fp-form__label">Message *</label>
                  <textarea className="fp-form__input fp-form__textarea" placeholder="Votre message..." required rows={4} value={formData.message} onChange={e => setFormData(p => ({ ...p, message: e.target.value }))} />
                </div>
                <button type="submit" className="btn-primary" style={{ fontSize: '14px', padding: '13px 28px', border: 'none', cursor: 'pointer' }}>
                  Envoyer le message →
                </button>
              </form>
            )}
          </div>
          <div className="fp-contact__right">
            <img src={fp40iaImages.contactFormImg} alt="FORPRODE 40 IA" className="fp-contact__img" />
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────── */}
      <section className="section" id="faq">
        <div style={{ textAlign: 'center' }}>
          <div className="section-eyebrow" style={{ display: 'inline-flex' }}>FAQ</div>
          <h2 className="section-title">Questions fréquentes</h2>
          <p className="section-subtitle" style={{ textAlign: 'center', margin: '0 auto 44px' }}>Tout ce que vous devez savoir sur le programme FORPRODE 40 IA.</p>
        </div>
        <div className="fp-faq">
          {faqItems.map((item, i) => (
            <div key={i} className={`fp-faq__item ${openFaq === i ? 'open' : ''}`}>
              <button className="fp-faq__question" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                <span>{item.q}</span>
                <svg className="fp-faq__chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
              {openFaq === i && (
                <div className="fp-faq__answer">{item.a}</div>
              )}
            </div>
          ))}
        </div>
        <div style={{ textAlign: 'center', marginTop: '32px' }}>
          <p style={{ fontSize: '15px', color: '#666', marginBottom: '12px' }}>Vous avez une autre question ?</p>
          <a href="#contact" style={{ fontFamily: "'Sora',sans-serif", fontSize: '14px', fontWeight: 700, color: 'var(--mid)', textDecoration: 'none' }}>
            Contactez-nous directement →
          </a>
        </div>
      </section>

      {/* ── BANNER PARTENAIRES ────────────────────────────── */}
      <section className="section section-alt">
        <img
          src={fp40iaImages.footerBanner}
          alt="Partenaires FORPRODE 40 IA"
          className="fp-banner-img"
        />
      </section>

      {/* ── CTA (DERNIER) ────────────────────────────────── */}
      <section className="fp-cta-section">
        <div className="fp-cta-section__glow" />
        <div className="fp-cta-section__inner">
          <div className="fp-cta-section__badge">
            <span className="forprode-page__badge-dot" />
            <span>Candidatures bientôt ouvertes</span>
          </div>
          <h2 className="fp-cta-section__title">
            Rejoignez les <span className="grad-text">50 talents</span> sélectionnés
          </h2>
          <p className="fp-cta-section__sub">
            Transformez votre carrière en 12 semaines. Les candidatures ouvrent le 6 Avril 2026.
          </p>
          <div className="fp-cta-section__actions">
            <a href={GOOGLE_FORM_URL} target="_blank" rel="noopener noreferrer" className="btn-primary" style={{ fontSize: '14px', padding: '14px 30px' }}>
              Candidater maintenant →
            </a>
            <Link to="/contact" style={{ fontFamily: "'Sora',sans-serif", fontSize: '14px', fontWeight: 500, padding: '14px 22px', background: 'transparent', border: '1px solid rgba(255,255,255,.2)', color: 'rgba(255,255,255,.75)', borderRadius: '9px', textDecoration: 'none' }}>
              Poser une question
            </Link>
          </div>
        </div>
      </section>

    </main>
  );
}
